import React, { useEffect, useState } from "react";
import axios from "axios";
import { Receipt, Upload, X, DollarSign, CheckCircle2, Clock } from "lucide-react";
import { Button, Card, Input } from "../../shared/ui";
import { uploadReceipt } from "../../lib/uploadReceipt";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: "fuel", label: "Fuel" },
  { value: "materials", label: "Materials / Hardware" },
  { value: "tools", label: "Tools / Equipment" },
  { value: "food", label: "Food / Drinks" },
  { value: "parking", label: "Parking / Tolls" },
  { value: "accommodation", label: "Accommodation" },
  { value: "other", label: "Other" },
];

/** Worker uploads receipts while clocked into a job.
 *  - Company-paid → just logged & attached to the job report
 *  - Personal card → also pushed to the worker's weekly timesheet for reimbursement
 *
 *  Props:
 *    clockedIn: bool — whether the worker is currently clocked in (toggles upload UI)
 *    activeJob: { job_id, job_title } | null — for displaying context
 *    onChanged: () => void — fired after a successful upload
 */
export const OnShiftReceiptCard = ({ clockedIn, activeJob, onChanged }) => {
  const [receipts, setReceipts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    amount: "",
    description: "",
    category: "other",
    vendor: "",
    payment_type: "company_card",
  });
  const [pickedFile, setPickedFile] = useState(null);

  const loadReceipts = async () => {
    try {
      const r = await axios.get(`${API}/time/on-shift-receipts`, { params: { limit: 20 } });
      setReceipts(r.data || []);
    } catch (e) {
      // silent
    }
  };
  useEffect(() => { loadReceipts(); }, [clockedIn, activeJob?.job_id]);

  const reset = () => {
    setForm({ amount: "", description: "", category: "other", vendor: "", payment_type: "company_card" });
    setPickedFile(null);
    setShowForm(false);
  };

  const save = async () => {
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) { toast.error("Enter the amount"); return; }
    if (!form.description.trim()) { toast.error("What was the receipt for?"); return; }
    if (!pickedFile) { toast.error("Please attach a photo of the receipt"); return; }

    setSaving(true);
    try {
      const up = await uploadReceipt(pickedFile);
      const r = await axios.post(`${API}/time/on-shift-receipt`, {
        receipt_url: up.url,
        amount: amt,
        description: form.description.trim(),
        category: form.category,
        vendor: form.vendor.trim() || null,
        payment_type: form.payment_type,
      });
      toast.success(r.data.message || "Receipt logged");
      reset();
      loadReceipts();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Receipt upload failed");
    } finally { setSaving(false); }
  };

  // Don't render if not clocked in
  if (!clockedIn) return null;

  const todays = receipts.slice(0, 5);

  return (
    <Card className="border-2 border-blue-200 bg-blue-50/30" data-testid="on-shift-receipt-card">
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg bg-blue-100 text-blue-700">
          <Receipt className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div>
              <h3 className="font-bold uppercase text-sm tracking-tight">On-shift Receipts</h3>
              <p className="text-xs text-slate-500">
                {activeJob?.job_title ? `Logged to ${activeJob.job_title}` : "Logged to your current job"}
              </p>
            </div>
            {!showForm && (
              <Button size="sm" onClick={() => setShowForm(true)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="on-shift-add-btn">
                <Upload className="w-3.5 h-3.5 mr-1" /> Add receipt
              </Button>
            )}
          </div>

          {showForm && (
            <div className="mt-3 space-y-2 p-3 bg-white border-2 border-blue-300 rounded" data-testid="on-shift-receipt-form">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] uppercase font-bold text-slate-500">Amount ($)</label>
                  <Input type="number" step="0.01" value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    placeholder="42.50" className="h-9 text-sm" data-testid="on-shift-amount" />
                </div>
                <div>
                  <label className="text-[10px] uppercase font-bold text-slate-500">Category</label>
                  <select value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="w-full h-9 text-sm border-2 border-slate-200 rounded px-2" data-testid="on-shift-category">
                    {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase font-bold text-slate-500">Description</label>
                <Input value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="e.g. 30L diesel for excavator" className="h-9 text-sm" data-testid="on-shift-description" />
              </div>
              <div>
                <label className="text-[10px] uppercase font-bold text-slate-500">Vendor (optional)</label>
                <Input value={form.vendor}
                  onChange={(e) => setForm({ ...form, vendor: e.target.value })}
                  placeholder="e.g. BP Servo" className="h-9 text-sm" />
              </div>

              {/* Paid by toggle — biggest decision the worker makes */}
              <div>
                <label className="text-[10px] uppercase font-bold text-slate-500 block mb-1">Paid by</label>
                <div className="grid grid-cols-2 gap-2">
                  <button type="button"
                    onClick={() => setForm({ ...form, payment_type: "company_card" })}
                    className={`p-2 border-2 rounded text-xs font-bold text-left transition-colors ${form.payment_type === "company_card" ? "border-blue-500 bg-blue-100 text-blue-900" : "border-slate-200 bg-white text-slate-600 hover:border-blue-200"}`}
                    data-testid="on-shift-paid-company">
                    <DollarSign className="w-3.5 h-3.5 inline mr-0.5" /> COMPANY CARD
                    <p className="text-[10px] font-normal mt-0.5 normal-case">Just keep on record</p>
                  </button>
                  <button type="button"
                    onClick={() => setForm({ ...form, payment_type: "personal_card" })}
                    className={`p-2 border-2 rounded text-xs font-bold text-left transition-colors ${form.payment_type === "personal_card" ? "border-amber-500 bg-amber-100 text-amber-900" : "border-slate-200 bg-white text-slate-600 hover:border-amber-200"}`}
                    data-testid="on-shift-paid-personal">
                    <Receipt className="w-3.5 h-3.5 inline mr-0.5" /> MY OWN
                    <p className="text-[10px] font-normal mt-0.5 normal-case">Reimburse on pay week</p>
                  </button>
                </div>
              </div>

              <div>
                <label className="text-[10px] uppercase font-bold text-slate-500">Receipt photo</label>
                <label className="block mt-1 px-3 py-3 border-2 border-dashed border-blue-300 rounded text-center text-xs text-slate-600 cursor-pointer hover:bg-blue-50">
                  <input type="file" accept="image/*,application/pdf" className="hidden"
                    onChange={(e) => setPickedFile(e.target.files?.[0] || null)}
                    data-testid="on-shift-file" />
                  {pickedFile ? (
                    <span className="font-semibold text-blue-700"><Upload className="w-3.5 h-3.5 inline mr-1" />{pickedFile.name}</span>
                  ) : (
                    <span><Upload className="w-3.5 h-3.5 inline mr-1" />Tap to pick a photo</span>
                  )}
                </label>
              </div>

              <div className="flex justify-end gap-2 pt-1">
                <Button size="sm" variant="ghost" onClick={reset} disabled={saving}>
                  <X className="w-3.5 h-3.5 mr-1" /> Cancel
                </Button>
                <Button size="sm" onClick={save} disabled={saving}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="on-shift-save">
                  {saving ? "Uploading..." : "Save receipt"}
                </Button>
              </div>
            </div>
          )}

          {todays.length > 0 && (
            <div className="mt-3 pt-3 border-t border-blue-200/60">
              <p className="text-[11px] font-bold uppercase text-slate-500 mb-1">Recent</p>
              <div className="space-y-1.5">
                {todays.map(r => (
                  <div key={r.id} className="flex items-center gap-2 text-xs bg-white border border-slate-200 rounded p-1.5" data-testid={`on-shift-receipt-${r.id}`}>
                    <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${r.payment_type === "company_card" ? "bg-blue-100 text-blue-700" : "bg-amber-100 text-amber-700"}`}>
                      {r.payment_type === "company_card" ? "Company" : "Reimburse"}
                    </span>
                    <span className="font-bold text-slate-800">${(r.amount || 0).toFixed(2)}</span>
                    <span className="text-slate-500 truncate flex-1">{r.description}</span>
                    {r.needs_reimbursement && r.reimbursement_status === "pending_approval" && (
                      <span title="On weekly timesheet for approval"><Clock className="w-3 h-3 text-amber-600" /></span>
                    )}
                    {!r.needs_reimbursement && <CheckCircle2 className="w-3 h-3 text-green-600" />}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

export default OnShiftReceiptCard;
